import { useState } from "react";

export default function ManivelleCasse() {
  const [open, setOpen] = useState<number | null>(null);

  const symptomes = [
    "La manivelle tourne dans le vide sans actionner le tablier",
    "Le volet ne monte plus malgré les tours de manivelle",
    "La manivelle tourne avec difficulté ou bloque à mi-course",
    "Cliquetis ou grincement mécanique lors de la manœuvre",
    "La manivelle se décroche ou sort de son logement",
    "Le tablier descend tout seul après une montée manuelle",
  ];

  const causes = [
    "Usure des pignons du treuil interne (dents érodées ou brisées)",
    "Rupture du cardan de transmission entre manivelle et treuil",
    "Déboîtement de la sortie de caisson ou pièce de manœuvre",
    "Casse du ressort de rappel ou du cliquet d'anti-retour",
    "Oxydation ou corrosion des pièces métalliques du mécanisme",
    "Surcharge répétée due à un tablier lourd ou mal équilibré",
  ];

  return (
    <section id="panne" className="bg-white py-14 md:py-20">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <h2 className="text-2xl md:text-3xl font-bold text-brand-blue mb-6">
          Manivelle cassée ou qui tourne dans le vide
        </h2>
        <p className="text-brand-text leading-relaxed mb-4">
          La manivelle est l'interface tactile entre vous et votre volet roulant manuel. Lorsqu'elle tourne dans le vide ou ne transmet plus la force de manœuvre au tablier, c'est souvent un problème de mécanisme interne : le treuil, le cardan ou la sortie de caisson est endommagé. Contrairement à une panne électrique qui touche le moteur, une panne de manivelle est purement mécanique et réside dans la chaîne de transmission entre la poignée et l'enrouleur.
        </p>
        <p className="text-brand-text leading-relaxed mb-8">
          Chez Dépan'Volets Strasbourg, nous intervenons sur toutes les marques de coffres : aluminium, PVC, acier galvanisé, avec manivelles droites, coudées, télescopiques ou à cliquet. Nous diagnostiquons précisément si la défaillance vient de la manivelle elle-même, du treuil interne, du cardan de liaison ou de la sortie de caisson. Chaque composant est inspecté avant de proposer un remplacement ciblé, pour ne pas changer inutilement l'ensemble du mécanisme.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-brand-light rounded-xl p-5 border border-brand-border">
            <h3 className="text-lg font-semibold text-brand-blue mb-4 flex items-center gap-2">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-cyan/10 text-brand-cyan">
                <i className="ri-error-warning-line"></i>
              </span>
              Symptômes d'une manivelle en panne
            </h3>
            <ul className="space-y-2">
              {symptomes.map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-brand-text">
                  <i className="ri-checkbox-circle-line text-brand-cyan mt-0.5 shrink-0"></i>
                  {s}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-brand-light rounded-xl p-5 border border-brand-border">
            <h3 className="text-lg font-semibold text-brand-blue mb-4 flex items-center gap-2">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-cyan/10 text-brand-cyan">
                <i className="ri-tools-line"></i>
              </span>
              Causes les plus fréquentes
            </h3>
            <ul className="space-y-2">
              {causes.map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-brand-text">
                  <i className="ri-checkbox-circle-line text-brand-cyan mt-0.5 shrink-0"></i>
                  {c}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-brand-border p-5">
          <h3 className="text-lg font-semibold text-brand-blue mb-4">
            Comment diagnostiquer une manivelle qui tourne dans le vide
          </h3>
          <div className="space-y-3">
            {[
              { q: "Retirez la manivelle et inspectez l'emboîtement", a: "Si la sortie de caisson est ronde, hexagonale ou carrée, vérifiez qu'elle n'est pas usée ou déformée. Une sortie rondie ne transmet plus la rotation." },
              { q: "Testez le mouvement sans la manivelle", a: "Utilisez une pince multiprise pour tourner la sortie de caisson directement. Si le tablier répond, le problème vient de la manivelle ou du cardan." },
              { q: "Écoutez le bruit du treuil à l'intérieur du coffre", a: "Un cliquetis régulier indique des pignons usés. Un silence total suggère une rupture totale du treuil ou du cardan." },
              { q: "Vérifiez l'état du tablier et de l'enroulement", a: "Un tablier trop lourd ou bloqué dans les coulisses surcharge le mécanisme et provoque la casse progressive du treuil." },
            ].map((item, i) => (
              <div key={i} className="border border-brand-border rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpen(open === i ? null : i)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-brand-light/50 transition-colors"
                >
                  <span className="font-medium text-brand-blue text-sm md:text-base">{item.q}</span>
                  <i className={`ri-arrow-down-s-line text-brand-cyan transition-transform ${open === i ? "rotate-180" : ""}`}></i>
                </button>
                {open === i && (
                  <div className="px-4 pb-4 text-sm text-brand-text leading-relaxed">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}